import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';
import { API } from '../config';
import { getErrorMessage } from '../utils/errorHandler';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchCurrentUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchCurrentUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      console.log('Get Current User API Response:', response.data);
      // Handle both response structures
      const userData = response.data.user || response.data.data?.user;
      if (userData) {
        setUser(userData);
      } else {
        console.error('Missing user data in response:', response.data);
        logout();
      }
    } catch (error) {
      console.error('Error fetching user:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (username, password) => {
    try {
      const response = await axios.post(`${API}/auth/login`, {
        username,
        password
      });
      console.log('Login API Response:', response.data);
      // Handle both response structures
      const newToken = response.data.token || response.data.data?.token;
      const userData = response.data.user || response.data.data?.user;
      
      if (!newToken || !userData) {
        console.error('Missing token or user data in response:', response.data);
        return {
          success: false,
          message: 'Invalid response from server'
        };
      }
      
      setToken(newToken);
      setUser(userData);
      localStorage.setItem('token', newToken);
      axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
      return { success: true };
    } catch (error) {
      console.error('Login error:', error);
      return {
        success: false,
        message: getErrorMessage(error)
      };
    }
  };

  const logout = () => {
    setToken(null);
    setUser(null);
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
  };

  const value = {
    user,
    token,
    login,
    logout,
    loading
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
